import * as RxDialog from '@radix-ui/react-dialog';
import { formatCode } from '@pl/domain';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useMatches } from 'react-router';
import { LiveProvider } from '../lib/useLive';
import { SiteTag } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { useQueue, useQueueState } from './queue/QueueProvider';
import { registerRefusalCloser } from './refusalGuard';
import s from './ScanLayout.module.css';
import { useScanSite } from './useScanSite';

export interface ScanRouteHandle {
  /** Shown as the screen title; the home screen has none, so its site chip sits on the left. */
  title?: string;
  /** Where the back button goes. Omitted on screens a worker should not back out of. */
  back?: string;
}

/**
 * The phone shell every scan screen sits in: one header carrying the back button, the title, the
 * site this phone is working at, and — on the right, where it is visible from every screen — the
 * state of the offline queue. A worker who has scanned forty boxes behind a stack of pallets has
 * one question, and the answer to it is always on screen.
 */
export function ScanLayout() {
  // Read once, here, and handed down: `useScanSite` re-reads its storage when its own state is
  // bumped, so a second copy up here would not hear the shell switch site, and the stream would stay
  // on the old one.
  const scanSite = useScanSite();
  // The phone streams its own site, so another phone's scans on the same order reach this one.
  return (
    <LiveProvider site={scanSite.site?.id ?? null} surface="scan">
      <ScanShell {...scanSite} />
    </LiveProvider>
  );
}

function ScanShell({ site, sites, setSiteId }: ReturnType<typeof useScanSite>) {
  const queue = useQueue();
  const { pending, sending, attention, storageOk } = useQueueState();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const matches = useMatches();

  // Both sheets live here in the shell, present on every scan screen, while a refusal can be
  // rendered by whichever page is routed into `Outlet` below — a different part of the tree
  // entirely. Mirrored into refs so the one refusal closer, registered once, always acts on
  // current state instead of being re-registered on every open/close.
  const sheetOpenRef = useRef(sheetOpen);
  sheetOpenRef.current = sheetOpen;
  const queueOpenRef = useRef(queueOpen);
  queueOpenRef.current = queueOpen;
  // Set only for the sheet actually closed *because a refusal arrived*, so an ordinary close
  // (its own Close button, Escape) still gets Radix's normal focus-return to the queue pill or
  // the site chip that opened it.
  const suppressSiteFocus = useRef(false);
  const suppressQueueFocus = useRef(false);

  useEffect(
    () =>
      registerRefusalCloser(() => {
        if (sheetOpenRef.current) {
          suppressSiteFocus.current = true;
          setSheetOpen(false);
        }
        if (queueOpenRef.current) {
          suppressQueueFocus.current = true;
          setQueueOpen(false);
        }
      }),
    [],
  );

  const handle = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const h = matches[i]!.handle as ScanRouteHandle | undefined;
      if (h) return h;
    }
    return {} as ScanRouteHandle;
  }, [matches]);

  const canSwitch = sites.length > 1;
  // Home has room for the full chip, and is where switching site belongs — between jobs, not in
  // the middle of one. A titled screen shows the site as the mockups do, under the title, where
  // it cannot collide with a long title and offers no half-sized control to fumble for.
  const siteChip = !site ? null : canSwitch ? (
    <button type="button" className={s.siteChip} onClick={() => setSheetOpen(true)}>
      <SiteTag site={site} />
      <span className={s.siteName}>{site.name}</span>
      <Icon name="chev-down" size={20} />
    </button>
  ) : (
    <span className={s.siteChip}>
      <SiteTag site={site} />
      <span className={s.siteName}>{site.name}</span>
    </span>
  );

  // Three states, never blended: something the worker has to look at, something still going, or
  // nothing outstanding. "All synced" is only ever shown when it is true of every scan — and a
  // phone whose storage would not answer cannot say that about any of them: what it counts is
  // what it happens to hold in memory, while the disk may hold scans this session never read.
  const tone = attention.length > 0 ? 'bad' : pending > 0 || !storageOk ? 'warn' : 'ok';
  const pillClass = { ok: s.pillOk, warn: s.pillWarn, bad: s.pillBad }[tone];
  const pillText = tone === 'bad' ? `${attention.length} to check` : tone === 'warn' ? (pending > 0 ? `${pending} waiting` : 'Not saved') : 'All synced';
  const pillIcon = tone === 'bad' ? 'alert' : tone === 'warn' ? (pending > 0 ? 'wifi-off' : 'alert') : 'check';
  const statusLine =
    attention.length > 0
      ? 'Some scans came back refused. The rest are safe.'
      : sending > 0
        ? 'Sending now…'
        : pending > 0
          ? `${pending} ${pending === 1 ? 'scan is' : 'scans are'} waiting for a signal. They send on their own.`
          : storageOk
            ? 'Every scan has reached the server.'
            : 'Every scan this app is holding has reached the server.';

  return (
    <div className="scan-app">
      <div className="scan-screen">
        <header className={`scan-head ${s.head}`}>
          {handle.title ? (
            <>
              {handle.back && (
                <Link to={handle.back} className={`back ${s.backBtn}`} aria-label="Back">
                  <Icon name="chev-left" />
                </Link>
              )}
              <div className="ttl">
                <h3 className={s.title}>{handle.title}</h3>
                {site && (
                  <div className="sub">
                    <SiteTag site={site} />
                    {site.name}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {siteChip}
              <span className={s.spacer} />
            </>
          )}

          <button type="button" className={`${s.queuePill} ${pillClass}`} onClick={() => setQueueOpen(true)}>
            <Icon name={pillIcon} size={18} />
            <span className={s.queueText}>{pillText}</span>
          </button>
        </header>

        <Outlet />

        {canSwitch && (
          <RxDialog.Root open={sheetOpen} onOpenChange={setSheetOpen}>
            <RxDialog.Portal>
              <RxDialog.Overlay className={s.sheetScrim} />
              <RxDialog.Content
                className={s.sheet}
                aria-describedby={undefined}
                onCloseAutoFocus={(e) => {
                  if (!suppressSiteFocus.current) return;
                  e.preventDefault();
                  suppressSiteFocus.current = false;
                }}
              >
                <RxDialog.Title className={s.sheetTitle}>Working at</RxDialog.Title>
                <div className={s.sheetList}>
                  {sites.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={s.sheetItem}
                      aria-pressed={option.id === site?.id}
                      onClick={() => {
                        setSiteId(option.id);
                        setSheetOpen(false);
                      }}
                    >
                      <SiteTag site={option} />
                      <span className={s.sheetItemName}>{option.name}</span>
                      {option.id === site?.id && <Icon name="check" size={24} />}
                    </button>
                  ))}
                </div>
                <RxDialog.Close asChild>
                  <button type="button" className="bigbtn ghost">
                    Close
                  </button>
                </RxDialog.Close>
              </RxDialog.Content>
            </RxDialog.Portal>
          </RxDialog.Root>
        )}

        <RxDialog.Root open={queueOpen} onOpenChange={setQueueOpen}>
          <RxDialog.Portal>
            <RxDialog.Overlay className={s.sheetScrim} />
            <RxDialog.Content
              className={s.sheet}
              aria-describedby={undefined}
              onCloseAutoFocus={(e) => {
                if (!suppressQueueFocus.current) return;
                e.preventDefault();
                suppressQueueFocus.current = false;
              }}
            >
              <RxDialog.Title className={s.sheetTitle}>Needs attention</RxDialog.Title>
              <p className={s.queueStatus}>{statusLine}</p>
              {!storageOk && <p className={s.queueWarn}>This phone would not store the queue. Keep the app open until everything has sent.</p>}

              {attention.length > 0 ? (
                <ul className={s.attnList}>
                  {attention.map((item) => (
                    <li key={item.clientScanId} className={s.attnItem}>
                      <div className={s.attnBody}>
                        <b className="mono">{formatCode(item.request.code)}</b>
                        <p className={s.attnMsg}>{item.response?.message ?? item.error ?? 'The server would not take this scan.'}</p>
                      </div>
                      <button type="button" className={s.attnOk} onClick={() => void queue.acknowledge(item.clientScanId)}>
                        OK
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={s.queueEmpty}>Nothing to check.</p>
              )}

              <RxDialog.Close asChild>
                <button type="button" className="bigbtn ghost">
                  Close
                </button>
              </RxDialog.Close>
            </RxDialog.Content>
          </RxDialog.Portal>
        </RxDialog.Root>
      </div>
    </div>
  );
}
